import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Lightbulb, Sparkles, Star, Trash2, Repeat, ChevronRight,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PLATFORM_META, ALL_PLATFORMS } from "@/lib/platforms";

const TABS = [
  { key: "all", label: "All" },
  { key: "starred", label: "Starred" },
];

const POPULAR_TOPICS = [
  "SaaS founders",
  "Personal branding",
  "AI tools",
  "Startup marketing",
  "Remote work",
  "Career growth",
];

function buildDraftFromIdea(idea) {
  return [
    `Hook / Title: ${idea.title}`,
    "",
    `Angle: ${idea.angle}`,
    "",
    `Why this works: ${idea.why_it_works || "—"}`,
    "",
    "Expand the above into a fully-realized piece of content that captures the hook and angle. Add specific examples, keep it human, and end with a strong takeaway.",
  ].join("\n");
}

export default function Ideas() {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [formatFilter, setFormatFilter] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [brandVoiceInfo, setBrandVoiceInfo] = useState({ id: "", name: "" });
  const [ideasUsage, setIdeasUsage] = useState(null);

  const loadUsage = async () => {
    try {
      const { data } = await api.get("/ideas/usage");
      setIdeasUsage(data);
    } catch { /* ignore */ }
  };

  // Fetch default brand voice + usage
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/brand-voices");
        const list = data.items || [];
        if (list.length > 0) setBrandVoiceInfo({ id: list[0].id, name: list[0].name });
      } catch { /* ignore */ }
    })();
    loadUsage();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "starred") params.starred = true;
      if (formatFilter) params.format = formatFilter;
      const { data } = await api.get("/ideas", { params });
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load ideas");
    } finally {
      setLoading(false);
    }
  }, [tab, formatFilter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const useVoice = !!brandVoiceInfo.id;
      const { data } = await api.post("/ideas/generate", {
        topic: topic.trim() || null,
        count: 10,
        use_brand_voice: useVoice,
        brand_voice_id: useVoice ? brandVoiceInfo.id : null,
      });
      toast.success(
        data.brand_voice_applied
          ? `${data.ideas.length} fresh ideas · Voice: ${data.brand_voice_name}`
          : `${data.ideas.length} fresh ideas`
      );
      if (data.usage) setIdeasUsage(data.usage);
      // Reset to All tab so new ideas are visible
      setTab("all");
      setFormatFilter("");
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Idea generation failed";
      if (status === 429) {
        toast.error(detail);
        loadUsage();
      } else {
        toast.error(detail);
      }
    } finally {
      setGenerating(false);
    }
  };

  const toggleStar = async (idea) => {
    const prev = items;
    const nextStarred = !idea.starred;
    setItems((cur) => cur.map((i) => (i.id === idea.id ? { ...i, starred: nextStarred } : i)));
    try {
      const { data } = await api.patch(`/ideas/${idea.id}`, { starred: nextStarred });
      setItems((cur) => cur.map((i) => (i.id === idea.id ? data.idea : i)));
      if (tab === "starred" && !nextStarred) {
        setItems((cur) => cur.filter((i) => i.id !== idea.id));
      }
    } catch {
      setItems(prev);
      toast.error("Could not update");
    }
  };

  const handleDelete = async (idea) => {
    setDeletingId(idea.id);
    try {
      await api.delete(`/ideas/${idea.id}`);
      setItems((cur) => cur.filter((i) => i.id !== idea.id));
      toast.success("Idea removed");
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const repurpose = (idea) => {
    navigate("/dashboard", {
      state: {
        reuse: {
          content: buildDraftFromIdea(idea),
          formats: [idea.best_format],
          brand_voice_id: brandVoiceInfo.id || null,
          brand_voice_name: brandVoiceInfo.name || null,
        },
      },
    });
  };

  const activeFiltersCount = (tab !== "all" ? 1 : 0) + (formatFilter ? 1 : 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12 lg:px-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">— Never run dry</p>
            <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl">Content Ideas</h1>
            <p className="mt-3 max-w-2xl text-sm text-neutral-600">
              Fresh, opinionated content ideas on demand. Pick a topic (or leave blank) and get 10 ideas
              ready to remix — matched to your Brand Voice.
            </p>
          </div>
          {ideasUsage && (
            <div
              data-testid="ideas-usage-badge"
              className="border border-neutral-200 bg-white px-4 py-3 text-xs"
            >
              <span className="uppercase tracking-widest text-neutral-500">
                {ideasUsage.plan === "pro" ? "Pro daily" : "Free daily"}
              </span>
              <div className="mt-1 font-serif text-lg font-semibold">
                <span data-testid="ideas-usage-remaining">{ideasUsage.remaining}</span>
                <span className="text-neutral-400"> / {ideasUsage.limit} batches</span>
              </div>
            </div>
          )}
        </div>

        {/* Generator */}
        <div className="mb-10 border border-neutral-200 bg-white p-6">
          <label className="text-xs uppercase tracking-widest text-neutral-500">
            Topic, niche or audience <span className="text-neutral-300">(optional)</span>
          </label>
          <div className="mt-3 flex flex-col gap-3 md:flex-row">
            <Input
              data-testid="ideas-topic-input"
              value={topic}
              onChange={(e) => setTopic(e.target.value.slice(0, 200))}
              placeholder='e.g. "SaaS founders", "personal branding", "AI tools"'
              onKeyDown={(e) => { if (e.key === "Enter" && !generating) generate(); }}
              className="h-12 flex-1 rounded-none border-neutral-300 focus-visible:ring-1 focus-visible:ring-black"
            />
            <Button
              onClick={generate}
              disabled={generating || (ideasUsage && ideasUsage.remaining <= 0)}
              data-testid="ideas-generate-btn"
              className="h-12 rounded-[3px] bg-coral px-8 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral disabled:opacity-60"
            >
              {generating ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Brewing…
                </>
              ) : ideasUsage && ideasUsage.remaining <= 0 ? (
                <>Daily limit reached</>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Generate 10 ideas
                </>
              )}
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Popular:</span>
            {POPULAR_TOPICS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTopic(t)}
                data-testid={`ideas-topic-preset-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-full border border-neutral-300 bg-white px-3 py-1 text-xs text-neutral-700 hover:border-black hover:text-black"
              >
                {t}
              </button>
            ))}
          </div>
          {brandVoiceInfo.id && (
            <p className="mt-4 text-xs text-neutral-500">
              ✨ Voice <span className="font-medium text-neutral-800">{brandVoiceInfo.name}</span> will be applied to new ideas.
            </p>
          )}
        </div>

        {/* Filters */}
        <div className="mb-4 flex" data-testid="ideas-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`ideas-tab-${t.key}`}
              className={`flex-1 border py-3 text-sm font-medium transition-colors md:flex-none md:px-8 ${
                tab === t.key
                  ? "border-coral bg-coral-subtle text-coral"
                  : "border-neutral-200 bg-white text-neutral-600 hover:border-black hover:text-black"
              } -ml-px first:ml-0`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mb-6 border border-neutral-200 bg-white p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Format:</span>
            <button
              type="button"
              onClick={() => setFormatFilter("")}
              data-testid="ideas-filter-format-all"
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                formatFilter === ""
                  ? "border-coral bg-coral-subtle text-coral"
                  : "border-neutral-300 bg-white text-neutral-700 hover:border-black hover:text-black"
              }`}
            >
              All
            </button>
            {ALL_PLATFORMS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFormatFilter(formatFilter === key ? "" : key)}
                data-testid={`ideas-filter-format-${key}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  formatFilter === key
                    ? "border-coral bg-coral-subtle text-coral"
                    : "border-neutral-300 bg-white text-neutral-700 hover:border-black hover:text-black"
                }`}
              >
                {PLATFORM_META[key].short}
              </button>
            ))}
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={() => { setTab("all"); setFormatFilter(""); }}
                data-testid="ideas-clear-filters"
                className="ml-auto text-xs text-neutral-500 underline underline-offset-4 hover:text-black"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {!loading && (
          <p className="mb-4 text-xs uppercase tracking-widest text-neutral-500" data-testid="ideas-count">
            {items.length} idea{items.length !== 1 ? "s" : ""}
            {activeFiltersCount > 0 ? " (filtered)" : ""}
          </p>
        )}

        {loading ? (
          <div className="flex justify-center py-24" data-testid="ideas-loading">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-neutral-300 bg-white p-16 text-center" data-testid="ideas-empty">
            <Lightbulb className="mx-auto mb-4 h-8 w-8 text-neutral-500" strokeWidth={1.5} />
            <p className="font-serif text-2xl italic text-neutral-500">
              {activeFiltersCount > 0 ? "Nothing matches those filters." : "No ideas yet."}
            </p>
            <p className="mt-2 max-w-md mx-auto text-sm text-neutral-500">
              {activeFiltersCount > 0
                ? "Try clearing the filters to see all your ideas."
                : "Pick a topic above (or leave blank) and get 10 fresh, opinionated angles in under a minute — matched to your Brand Voice."}
            </p>
            {activeFiltersCount > 0 ? (
              <div className="mt-6">
                <Button
                  onClick={() => { setTab("all"); setFormatFilter(""); }}
                  data-testid="ideas-empty-clear-btn"
                  className="h-11 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                <span className="text-xs uppercase tracking-widest text-neutral-500 mr-1">Try:</span>
                {POPULAR_TOPICS.slice(0, 4).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTopic(t)}
                    data-testid={`ideas-empty-topic-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded-full border border-neutral-300 bg-white px-3 py-1 text-xs text-neutral-700 hover:border-black hover:text-black"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((idea) => (
              <div
                key={idea.id}
                data-testid={`idea-card-${idea.id}`}
                className={`border bg-white transition-colors ${idea.starred ? "border-black" : "border-neutral-200"}`}
              >
                <div className="p-6">
                  <div className="mb-3 flex items-start justify-between gap-4">
                    <h3
                      data-testid={`idea-title-${idea.id}`}
                      className="min-w-0 flex-1 break-words font-serif text-xl font-semibold tracking-tight text-neutral-900"
                    >
                      {idea.title}
                    </h3>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggleStar(idea)}
                        data-testid={`idea-star-${idea.id}`}
                        className={`p-2 ${idea.starred ? "text-black" : "text-neutral-500 hover:text-black"}`}
                        aria-label={idea.starred ? "Unstar" : "Star"}
                        aria-pressed={idea.starred}
                      >
                        <Star className={`h-4 w-4 ${idea.starred ? "fill-black" : ""}`} strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(idea)}
                        disabled={deletingId === idea.id}
                        data-testid={`idea-delete-${idea.id}`}
                        className="p-2 text-neutral-500 hover:text-black"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  <p className="text-sm leading-relaxed text-neutral-700">{idea.angle}</p>

                  {idea.why_it_works && (
                    <p className="mt-3 border-l-2 border-neutral-200 pl-3 text-xs italic text-neutral-500">
                      Why it works — {idea.why_it_works}
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 pt-4">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                      <span className="border border-neutral-200 bg-neutral-50 px-2 py-1 uppercase tracking-widest">
                        {PLATFORM_META[idea.best_format]?.short || idea.best_format}
                      </span>
                      {idea.topic && (
                        <span className="text-neutral-400">· topic: {idea.topic}</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => repurpose(idea)}
                      data-testid={`idea-repurpose-${idea.id}`}
                      className="h-10 rounded-[3px] bg-neutral-900 text-xs text-white hover:bg-neutral-800"
                    >
                      <Repeat className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} />
                      Repurpose this idea
                      <ChevronRight className="ml-1 h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {/* Generate more CTA at bottom of list */}
            <div className="pt-4 text-center">
              <Button
                onClick={generate}
                disabled={generating || (ideasUsage && ideasUsage.remaining <= 0)}
                data-testid="ideas-generate-more-btn"
                variant="outline"
                className="h-12 rounded-none border-neutral-300 px-8 disabled:opacity-60"
              >
                {generating ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
                    Brewing…
                  </>
                ) : ideasUsage && ideasUsage.remaining <= 0 ? (
                  <>Daily limit reached — back tomorrow</>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                    Generate 10 more ideas
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
